import {useState, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate, useSearchParams, Link} from 'react-router-dom';
import {PASSWORD_REQUIREMENTS, validateStrongPassword} from '@common/validation/strongPassword';
import {toast} from '@common-web/hooks/use-toast';
import {Button} from '@common-web/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@common-web/components/ui/card';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {trpc} from '@common-web/lib/trpc';
import {Lock, Eye, EyeOff, CheckCircle, AlertTriangle} from 'lucide-react';
import {AuthPageLayout} from '@common-web/components/auth-page-layout';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);

  const {
    register,
    handleSubmit,
    formState: {errors},
    watch,
  } = useForm<ResetPasswordFormData>();

  const resetPasswordMutation = trpc.auth.resetPassword.useMutation();

  const password = watch('password');

  useEffect(() => {
    if (!token) {
      toast({
        title: 'Invalid reset link',
        description: 'The password reset link is invalid or expired',
        variant: 'destructive',
      });
      navigate('/login');
    }
  }, [token, navigate]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    setIsLoading(true);

    try {
      await resetPasswordMutation.mutateAsync({
        token,
        password: data.password,
      });

      setPasswordReset(true);
      toast({
        title: 'Password reset successful',
        description: 'Your password has been updated successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reset password',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (passwordReset) {
    return (
      <AuthPageLayout>
        <div className='w-full max-w-md'>
          <Card className='border-gray-200 shadow-xl'>
            <CardHeader className='space-y-1 pb-6'>
              <div className='flex items-center justify-center mb-4'>
                <div className='p-3 bg-green-100 rounded-full'>
                  <CheckCircle className='h-6 w-6 text-green-600' />
                </div>
              </div>
              <CardTitle className='text-2xl text-center font-bold text-brand-navy'>
                Password Reset Complete
              </CardTitle>
              <CardDescription className='text-center text-brand-gray'>
                Your password has been successfully updated
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                <div className='p-4 bg-green-50 rounded-lg border border-green-200'>
                  <p className='text-sm text-green-800'>
                    <strong>Success!</strong> Your password has been changed. You can now sign in
                    with your new password.
                  </p>
                </div>

                <Link to='/login'>
                  <Button className='w-full h-11 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold transition-colors shadow-sm'>
                    Continue to Sign In
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </AuthPageLayout>
    );
  }

  if (!token) {
    return (
      <div className='min-h-screen flex items-center justify-center p-8 bg-gray-50'>
        <Card className='w-full max-w-md border-red-200 shadow-xl'>
          <CardHeader className='space-y-1 pb-6'>
            <div className='flex items-center justify-center mb-4'>
              <div className='p-3 bg-red-100 rounded-full'>
                <AlertTriangle className='h-6 w-6 text-red-600' />
              </div>
            </div>
            <CardTitle className='text-2xl text-center font-bold text-red-600'>
              Invalid Reset Link
            </CardTitle>
            <CardDescription className='text-center text-brand-gray'>
              This password reset link is invalid or has expired
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to='/forgot-password'>
              <Button className='w-full h-11 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold transition-colors shadow-sm'>
                Request New Reset Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AuthPageLayout>
      <div className='w-full max-w-md'>
        <Card className='border-gray-200 shadow-xl'>
          <CardHeader className='space-y-1 pb-6'>
            <div className='flex items-center justify-center mb-4'>
              <div className='p-3 bg-brand-navy/10 rounded-full'>
                <Lock className='h-6 w-6 text-brand-navy' />
              </div>
            </div>
            <CardTitle className='text-2xl text-center font-bold text-brand-navy'>
              Create New Password
            </CardTitle>
            <CardDescription className='text-center text-brand-gray'>
              Enter a new password for your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='flex flex-col gap-5'>
                <div className='space-y-2'>
                  <Label htmlFor='password' className='text-brand-navy font-medium'>
                    New Password
                  </Label>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='password'
                      type={showPassword ? 'text' : 'password'}
                      placeholder='••••••••'
                      className='pl-10 pr-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      disabled={isLoading}
                      {...register('password', {
                        required: 'Password is required',
                        validate: validateStrongPassword,
                      })}
                    />
                    <button
                      type='button'
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray hover:text-brand-navy'
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className='h-4 w-4' />
                      ) : (
                        <Eye className='h-4 w-4' />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className='text-sm text-red-600 mt-1'>{errors.password.message}</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='confirmPassword' className='text-brand-navy font-medium'>
                    Confirm New Password
                  </Label>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='confirmPassword'
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder='••••••••'
                      className='pl-10 pr-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      disabled={isLoading}
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) => value === password || 'Passwords do not match',
                      })}
                    />
                    <button
                      type='button'
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray hover:text-brand-navy'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className='h-4 w-4' />
                      ) : (
                        <Eye className='h-4 w-4' />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className='text-sm text-red-600 mt-1'>{errors.confirmPassword.message}</p>
                  )}
                </div>

                <div className='p-3 bg-blue-50 rounded-lg border border-blue-200'>
                  <p className='text-xs font-bold text-blue-800'>Password requirements:</p>
                  <ul className='list-disc pl-4 text-xs text-blue-800'>
                    {PASSWORD_REQUIREMENTS.map((requirement) => (
                      <li key={requirement}>{requirement}</li>
                    ))}
                  </ul>
                </div>

                <Button
                  type='submit'
                  className='w-full h-11 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold transition-colors shadow-sm'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className='flex items-center gap-2'>
                      <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                      Resetting Password...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </div>

              <div className='mt-6 text-center'>
                <Link
                  to='/login'
                  className='text-sm text-brand-blue hover:text-brand-blue-dark font-semibold transition-colors'
                >
                  Back to Sign In
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className='text-center text-xs text-brand-gray mt-6'>
          Protected by enterprise-grade security. Your data is encrypted and secure.
        </p>
      </div>
    </AuthPageLayout>
  );
}
